import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Loader2, Send, X } from "lucide-react";
import api from "../../lib/api";

export function DemoModal({ open, onClose }) {
    const [form, setForm] = useState({ name: "", email: "", mobile: "", company: "", message: "" });
    const [status, setStatus] = useState("idle"); // idle | sending | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (status === "sending") return; // prevent double-submit
        setStatus("sending");
        setErrorMsg("");
        try {
            await api.post("/contact/demo", {
                name: form.name.trim(),
                email: form.email.trim(),
                mobile: form.mobile.trim(),
                company: form.company.trim(),
                message: form.message.trim(),
            });
            setStatus("success");
        } catch (err) {
            if (err.response) {
                setErrorMsg(err.response.data?.detail || "Submission failed. Please try again.");
            } else if (err.request) {
                setErrorMsg("Unable to reach the server. Please check your connection and try again.");
            } else {
                setErrorMsg("Something went wrong. Please try again.");
            }
            setStatus("error");
        }
    };

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setForm({ name: "", email: "", mobile: "", company: "", message: "" });
            setStatus("idle");
            setErrorMsg("");
        }, 300);
    };

    if (!open) return null;

    const inputCls = "w-full text-sm text-slate-900 bg-white border border-slate-200 rounded-lg px-4 py-3 outline-none focus:border-[#4A7AB5] focus:ring-2 focus:ring-[#4A7AB5]/10 transition-all placeholder:text-slate-400";

    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                    data-testid="demo-modal-overlay">
                    <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={handleClose} />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 16 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
                        data-testid="demo-modal">
                        <div className="flex items-center justify-between px-6 pt-6 pb-2">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Request a Demo</h3>
                                <p className="text-sm text-slate-500 mt-0.5">See how K2B measures action readiness</p>
                            </div>
                            <button data-testid="demo-modal-close" onClick={handleClose}
                                className="p-2 -mr-2 rounded-lg hover:bg-slate-100 transition-colors">
                                <X className="h-5 w-5 text-slate-400" />
                            </button>
                        </div>

                        {status === "success" ? (
                            <div className="px-6 py-12 text-center" data-testid="demo-modal-success">
                                <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="h-7 w-7 text-emerald-500" />
                                </div>
                                <h4 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>Thank You!</h4>
                                <p className="text-sm text-slate-500 leading-relaxed mb-6">We've received your request. Our team will reach out to you shortly.</p>
                                <button data-testid="demo-modal-done-btn" onClick={handleClose}
                                    className="text-sm font-medium text-[#4A7AB5] hover:text-[#3d6596] transition-colors">Close</button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="px-6 pb-6 pt-4 space-y-3.5" data-testid="demo-form">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Name *</label>
                                    <input data-testid="demo-name" type="text" required value={form.name} onChange={set("name")}
                                        placeholder="Your full name" className={inputCls} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email *</label>
                                    <input data-testid="demo-email" type="email" required value={form.email} onChange={set("email")}
                                        placeholder="you@company.com" className={inputCls} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Mobile *</label>
                                    <input data-testid="demo-mobile" type="tel" required value={form.mobile} onChange={set("mobile")}
                                        placeholder="+91 98765 43210" className={inputCls} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Company *</label>
                                    <input data-testid="demo-company" type="text" required value={form.company} onChange={set("company")}
                                        placeholder="Company name" className={inputCls} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Message</label>
                                    <textarea data-testid="demo-message" rows={3} value={form.message} onChange={set("message")}
                                        placeholder="Tell us about your coaching needs (optional)" className={`${inputCls} resize-none`} />
                                </div>
                                {status === "error" && (
                                    <div data-testid="demo-error" className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-600">
                                        {errorMsg}
                                    </div>
                                )}
                                <button data-testid="demo-submit-btn" type="submit" disabled={status === "sending"}
                                    className="w-full text-sm font-semibold text-white bg-[#4A7AB5] hover:bg-[#3d6596] disabled:opacity-60 disabled:cursor-not-allowed px-6 py-3.5 rounded-lg transition-all shadow-sm hover:shadow-lg flex items-center justify-center gap-2 mt-2">
                                    {status === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <><Send className="h-4 w-4" /> Submit Request</>}
                                </button>
                            </form>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
